import { NavLink, Outlet, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, Mail, Sparkles, FileText, LifeBuoy,
  BarChart3, ExternalLink, LogOut, ShieldCheck, ArrowUpCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/applications", label: "Account Upgrades", icon: ArrowUpCircle },
  { to: "/admin/properties", label: "Property Moderation", icon: Building2 },
  { to: "/admin/contact", label: "Contact Messages", icon: Mail },
  { to: "/admin/ai", label: "AI Management", icon: Sparkles },
  { to: "/admin/blog", label: "Blog CMS", icon: FileText },
  { to: "/admin/help", label: "Help Center CMS", icon: LifeBuoy },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
];

// Reserved for later phases.
const SOON = [];

export const AdminLayout = () => {
  const { profile, signOut } = useAuth();

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-white lg:flex">
        <div className="flex h-16 items-center gap-2 border-b border-border px-6">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1E3A8A] text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <span className="text-lg font-bold text-slate-900">ABAY360 <span className="text-[#1E3A8A]">Admin</span></span>
        </div>
        <nav className="flex-1 space-y-1 p-3" data-testid="admin-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase().split(" ")[0]}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1E3A8A] text-white" : "text-slate-600 hover:bg-[#1E3A8A]/5 hover:text-[#1E3A8A]"
                }`
              }
            >
              <item.icon className="h-4 w-4" /> {item.label}
            </NavLink>
          ))}
          {SOON.length > 0 && <p className="px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Coming soon</p>}
          {SOON.map((item) => (
            <div key={item.label} className="flex cursor-not-allowed items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-300">
              <item.icon className="h-4 w-4" /> {item.label}
            </div>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <Link to="/" className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-[#1E3A8A]/5 hover:text-[#1E3A8A]">
            <ExternalLink className="h-4 w-4" /> View site
          </Link>
          <button onClick={signOut} data-testid="admin-signout" className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-[#C0392B]/5 hover:text-[#C0392B]">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-border bg-white px-4 sm:px-8">
          {/* Mobile nav */}
          <div className="flex items-center gap-2 lg:hidden">
            <ShieldCheck className="h-5 w-5 text-[#1E3A8A]" />
            <span className="font-bold text-slate-900">Admin</span>
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-slate-900" data-testid="admin-user-name">{profile?.full_name || "Administrator"}</p>
              <p className="text-xs capitalize text-slate-400">{profile?.role?.replace("_", " ")}</p>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E3A8A]/8 text-sm font-semibold text-[#1E3A8A]">
              {(profile?.full_name || profile?.email || "A").charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        {/* Mobile tab nav */}
        <div className="flex gap-1 overflow-x-auto border-b border-border bg-white px-3 py-2 lg:hidden">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ${
                  isActive ? "bg-[#1E3A8A] text-white" : "text-slate-600"
                }`
              }
            >
              <item.icon className="h-3.5 w-3.5" /> {item.label.split(" ")[0]}
            </NavLink>
          ))}
        </div>

        <main className="flex-1 p-4 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
